import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleHelp, Menu } from 'lucide-react';
import { SUPPORT_URL } from './HelpButton';
import { Logo } from './Logo';
import { MobileMenu } from './MobileMenu';
import { UserMenu } from './UserMenu';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LangSwitch } from '@/components/ui/LangSwitch';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { useNavLinks } from '@/hooks/useNavLinks';
import { cn } from '@/lib/cn';

/** Bo'lim ichidagi boshqa yo'llar ham shu menyu bandini faol qiladi (kasb sahifasi — "Kurslar") */
const ALSO_ACTIVE: Partial<Record<string, string>> = { '/courses': '/careers/' };

function isAlsoActive(to: string, pathname: string): boolean {
  const prefix = ALSO_ACTIVE[to];
  return prefix !== undefined && pathname.startsWith(prefix);
}

export function Navbar() {
  const { t } = useTranslation();
  const links = useNavLinks();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const { pathname } = useLocation();

  return (
    <>
      <header className="glass sticky top-0 z-50 border-b border-line">
        <div className="mx-auto flex h-16 max-w-8xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />

          <nav className="hidden items-center gap-1 md:flex">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    'rounded-DEFAULT px-3 py-2 text-sm font-medium transition-colors',
                    isActive || isAlsoActive(link.to, pathname)
                      ? 'bg-surface text-content'
                      : 'text-content-secondary hover:bg-surface hover:text-content',
                  )
                }
              >
                {t(link.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            {/* Yechish sahifasida suzuvchi tugma yo'q (editorni to'sadi) */}
            {pathname.includes('/problems/') && !pathname.startsWith('/problems/difficulty/') && (
              <a
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.helpOpen')}
                title={t('footer.help')}
                className="btn btn-ghost rounded-DEFAULT p-2"
              >
                <CircleHelp className="h-5 w-5" aria-hidden />
              </a>
            )}
            <LangSwitch />
            <ThemeToggle />

            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost ml-1 hidden px-3 py-2 text-sm sm:inline-flex">
                  {t('nav.login')}
                </Link>
                <Link to="/register">
                  <Button size="sm" className="hidden sm:inline-flex">
                    {t('nav.register')}
                  </Button>
                </Link>
              </>
            )}

            <button
              onClick={() => setMenuOpen(true)}
              className="btn btn-ghost rounded-DEFAULT p-2 md:hidden"
              aria-label={t('nav.openMenu')}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} links={links} />
    </>
  );
}
